<template>
  <!-- Баннер главной (макет владельца 12.10.2026): заголовок поверх картинки, поиск под товарами.
       Картинки: стандартные (hero-desktop.jpg / hero-mobile.jpg) или из админки
       (Сайт → Главная страница → Баннер). Линии нарисованы прямо на картинках. -->
  <section class="greeting">
    <div ref="hero" class="greeting__hero" :style="heroStyle">
      <div
        v-if="!isMobile"
        class="greeting__img"
        :class="{ 'greeting__img--narrow': imgBox && imgBox.narrow }"
        :style="imgStyle"
      ></div>
      <div class="container greeting__overlay">
        <div ref="info" class="greeting__info">
          <h2>{{ textOr(content?.heroTitle, 'Wide selection of industrial automation and electronics components.') }}</h2>
          <p>{{ textOr(content?.heroText, 'We are your reliable partner for solving problems in the field of industrial automation.') }}</p>
        </div>
      </div>
      <div v-if="isMobile" class="greeting__img-mobile" :style="mobileImgStyle"></div>
      <div class="container greeting__search-wrap" :style="searchStyle">
        <search-block
          class="greeting__hero-search"
          variant="hero"
          :placeholder="isMobile ? 'Part number, brand or keyword' : 'Search by part number, manufacturer or keyword...'"
        ></search-block>
      </div>
    </div>
  </section>
</template>

<script>
import SearchBlock from "@/components/ui/SearchBlock.vue";
import {textOr} from "@/functions/siteSettings";
import defaultDesktop from "@/assets/images/hero-desktop.jpg";
import defaultMobile from "@/assets/images/hero-mobile.jpg";

/*
 * Где на стандартной картинке товары (доля от высоты картинки):
 * компьютер 1916×1121 — товары примерно от 43% до 85%;
 * телефон 780×1040 — сверху фон цеха (на нём текст), низ товаров примерно на 92%.
 * Для картинки из админки берутся те же доли.
 */
const DESK = { top: 0.43, bottom: 0.855 };

export default {
  components: { SearchBlock },
  props: {
    content: { type: Object, default: null },
  },
  data() {
    return {
      isMobile: false,
      heroHeight: null,
      imgBox: null,
      searchBottom: null,
      ratio: 1121 / 1916,
      mobileRatio: 1040 / 780,
    };
  },
  computed: {
    desktopUrl() {
      return this.content?.heroBannerDesktop || defaultDesktop;
    },
    mobileUrl() {
      return this.content?.heroBannerMobile || defaultMobile;
    },
    heroStyle() {
      if (this.isMobile) return { backgroundImage: `url(${this.mobileUrl})` };
      return this.heroHeight ? { height: this.heroHeight + "px" } : {};
    },
    imgStyle() {
      const st = { backgroundImage: `url(${this.desktopUrl})` };
      const b = this.imgBox;
      if (b) {
        Object.assign(st, {
          left: b.left + "px", top: b.top + "px", width: b.width + "px", height: b.height + "px",
        });
      }
      return st;
    },
    mobileImgStyle() {
      // пустой блок высотой с картинку: сама картинка — фон всего баннера (и под текстом тоже)
      return { aspectRatio: String(1 / this.mobileRatio) };
    },
    searchStyle() {
      if (this.isMobile) return {}; // на телефоне поиск стоит на «полу» под товарами (CSS)
      return this.searchBottom !== null ? { bottom: this.searchBottom + "px" } : {};
    },
  },
  watch: {
    desktopUrl: { handler(u) { this.loadRatio(u, "ratio"); }, immediate: true },
    mobileUrl: { handler(u) { this.loadRatio(u, "mobileRatio"); }, immediate: true },
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", this.onResize, { passive: true });
    if (document.fonts?.ready) document.fonts.ready.then(() => this.onResize());
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    textOr,
    loadRatio(url, key) {
      if (!url || typeof Image === "undefined") return;
      const img = new Image();
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          this[key] = img.naturalHeight / img.naturalWidth;
          this.onResize();
        }
      };
      img.src = url;
    },
    onResize() {
      this.isMobile = window.innerWidth < 768;
      const hero = this.$refs.hero;
      if (this.isMobile || !hero) {
        this.heroHeight = null;
        this.imgBox = null;
        this.searchBottom = null;
        return;
      }
      const W = hero.clientWidth || window.innerWidth;
      const natural = W * this.ratio;                       // картинка во всю ширину
      const top = hero.getBoundingClientRect().top + window.scrollY;
      const free = window.innerHeight - top;                // место до низа экрана
      const info = this.$refs.info;
      const textBottom = info ? info.offsetTop + info.offsetHeight + 24 : 260; // низ заголовка + отступ
      const SEARCH = 64 + 6 + 28;                           // поиск под товарами + отступ снизу

      const h = Math.round(Math.min(Math.max(free, 460), natural * 1.05));
      // товары должны поместиться между текстом и поиском — иначе картинку уменьшаем целиком
      const imgH = Math.min(natural, (h - SEARCH - textBottom) / (DESK.bottom - DESK.top));
      const imgW = imgH / this.ratio;
      const imgTop = h - SEARCH - imgH * DESK.bottom;       // низ товаров — над поиском
      const productsBottom = imgTop + imgH * DESK.bottom;

      this.heroHeight = h;
      this.imgBox = {
        left: Math.round((W - imgW) / 2), top: Math.round(imgTop),
        width: Math.round(imgW), height: Math.round(imgH),
        narrow: imgW < W - 2,
      };
      this.searchBottom = Math.max(12, Math.round(h - productsBottom - 6 - 64));
    },
  },
};
</script>

<style scoped>
.greeting__hero {
  position: relative;
  overflow: hidden;
  height: 720px;
  background: #ecf0f5; /* цвет верха картинки — текст стоит на нём без шва */
}
.greeting__img {
  position: absolute;
  inset: 0;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  /* низ картинки плавно уходит в фон, если картинка ниже экрана */
  -webkit-mask-image: linear-gradient(to bottom, #000 92%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 92%, transparent 100%);
}
.greeting__img--narrow {
  -webkit-mask-image: linear-gradient(to right, transparent 0, #000 9%, #000 91%, transparent 100%);
  mask-image: linear-gradient(to right, transparent 0, #000 9%, #000 91%, transparent 100%);
}
.greeting__overlay {
  position: relative;
  z-index: 1;
}
.greeting__info {
  padding-top: 56px;
  max-width: 640px;
}
.greeting__info h2 {
  font-size: 46px;
  line-height: 100%;
  margin-bottom: 22px;
}
.greeting__info p {
  font-size: 18px;
  line-height: 130%;
  max-width: 520px;
}
.greeting__search-wrap {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 28px;
  z-index: 2;
  display: flex;
  justify-content: center;
}
.greeting__hero-search {
  width: 100%;
  max-width: 820px;
}

/* невысокие экраны ноутбуков — компактнее текст */
@media (min-width: 768px) and (max-height: 1000px) {
  .greeting__info {
    padding-top: 40px;
  }
  .greeting__info h2 {
    font-size: 38px;
    margin-bottom: 16px;
  }
  .greeting__info p {
    font-size: 17px;
  }
}

/* ===== Телефон: картинка — фон всего баннера, текст сверху по центру, товары целиком, поиск под ними ===== */
@media (max-width: 767px) {
  .greeting__hero {
    height: auto;
    background-color: #f3f5f9;
    background-repeat: no-repeat;
    background-position: center top;
    background-size: 100% auto;
    padding-bottom: 22px;
  }
  /* лёгкая светлая дымка под текстом — для читаемости */
  .greeting__hero::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 50%;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0));
    pointer-events: none;
  }
  .greeting__overlay {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
  }
  .greeting__info {
    padding-top: 30px;
    max-width: none;
    text-align: center;
  }
  .greeting__info h2 {
    font-size: 26px;
    margin: 0 auto 12px;
    max-width: 320px;
  }
  .greeting__info p {
    font-size: 14px;
    line-height: 140%;
    margin: 0 auto;
    max-width: 290px;
  }
  .greeting__img-mobile {
    width: 100%;
  }
  .greeting__search-wrap {
    position: absolute;
    bottom: 10px;
  }
}
</style>
